import * as THREE from 'three';
import { SKY_COLOR } from '../constants.js';

/**
 * Minecraft-style day/night cycle with sky dome, sun, moon, stars, and clouds.
 *
 * Full cycle = 20 minutes (1200 seconds).
 * Time of day: 0.0 = dawn, 0.25 = noon, 0.5 = dusk, 0.75 = midnight.
 *
 * Enhanced features:
 * - Gradient sky with horizon/zenith color separation
 * - Sun glow near horizon (dawn/dusk)
 * - Twinkling stars with individual opacity animation
 * - 8 moon phases cycling every 8 game days
 * - Sunrise/sunset warm glow on horizon side
 */

const CYCLE_DURATION = 1200;

// Sky color key-frames (horizon color)
const SKY_DAWN  = new THREE.Color(0xFF9944);
const SKY_DAY   = new THREE.Color(SKY_COLOR);
const SKY_DUSK  = new THREE.Color(0xFF6644);
const SKY_NIGHT = new THREE.Color(0x0A0A2A);

// Zenith (top of sky) colors — darker than horizon
const ZENITH_DAY   = new THREE.Color(0x0055BB);
const ZENITH_DAWN  = new THREE.Color(0x4444AA);
const ZENITH_DUSK  = new THREE.Color(0x3322AA);
const ZENITH_NIGHT = new THREE.Color(0x020210);

// Sun / moon light colors
const SUN_COLOR  = new THREE.Color(0xFFFFDD);
const MOON_COLOR = new THREE.Color(0x8888CC);

const ORBIT_RADIUS = 400;
const STAR_COUNT = 800;
const CLOUD_HEIGHT = 180;

// Moon phase config
const MOON_PHASE_COUNT = 8;  // 8 phases cycle every 8 game days

// Sky dome vertex shader
const SKY_VS = `
varying vec3 vDir;
void main() {
  vDir = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// Sky dome fragment shader — gradient from horizon to zenith with sun glow
const SKY_FS = `
uniform vec3 topColor;
uniform vec3 bottomColor;
uniform vec3 sunDir;
uniform vec3 sunGlowColor;
uniform float sunGlowIntensity;
varying vec3 vDir;
void main() {
  float h = max(vDir.y, 0.0);
  vec3 sky = mix(bottomColor, topColor, pow(h, 0.4));

  // Sun glow near horizon — wider and warmer
  float sunDot = max(dot(normalize(vDir), sunDir), 0.0);
  float glow = pow(sunDot, 8.0) * sunGlowIntensity * (1.0 - h * 0.5);
  sky += sunGlowColor * glow;

  // Secondary wider glow for sunrise/sunset atmosphere
  float wideGlow = pow(sunDot, 3.0) * sunGlowIntensity * 0.3 * (1.0 - h);
  sky += sunGlowColor * wideGlow;

  // Below horizon: darker
  if (vDir.y < 0.0) {
    float below = clamp(-vDir.y * 2.0, 0.0, 1.0);
    sky = mix(bottomColor, bottomColor * 0.3, below);
  }

  gl_FragColor = vec4(sky, 1.0);
}`;

export class DayNight {
  constructor(scene, directionalLight, ambientLight, fog) {
    this.scene = scene;
    this.directionalLight = directionalLight;
    this.ambientLight = ambientLight;
    this.fog = fog;

    // Start at early morning (t=0.15) so the sky is blue on first load
    this._elapsed = 0.15 * CYCLE_DURATION;
    // Track total elapsed for moon phases (counts full cycles)
    this._totalElapsed = this._elapsed;

    this._skyColor = new THREE.Color();
    this._zenithColor = new THREE.Color();
    this._tmpColor = new THREE.Color();

    // ---- Sky Dome (gradient background) ----
    this._skyUniforms = {
      topColor: { value: new THREE.Color(ZENITH_DAY) },
      bottomColor: { value: new THREE.Color(SKY_DAY) },
      sunDir: { value: new THREE.Vector3(0, 1, 0) },
      sunGlowColor: { value: new THREE.Color(0xFFAA44) },
      sunGlowIntensity: { value: 0.3 },
    };
    const skyGeo = new THREE.SphereGeometry(600, 32, 16);
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
      uniforms: this._skyUniforms,
      vertexShader: SKY_VS,
      fragmentShader: SKY_FS,
    });
    this.skyDome = new THREE.Mesh(skyGeo, skyMat);
    this.skyDome.renderOrder = -100;
    this.skyDome.frustumCulled = false;
    scene.add(this.skyDome);

    // ---- Sun mesh (big, visible disc) ----
    const sunGeo = new THREE.CircleGeometry(30, 32);
    const sunMat = new THREE.MeshBasicMaterial({
      color: 0xFFEE44,
      side: THREE.DoubleSide,
      fog: false,
      transparent: true,
      depthWrite: false,
    });
    this.sunMesh = new THREE.Mesh(sunGeo, sunMat);
    this.sunMesh.renderOrder = -1;
    this.sunMesh.frustumCulled = false;
    scene.add(this.sunMesh);

    // Sun glow (larger semi-transparent disc behind the sun)
    const glowGeo = new THREE.CircleGeometry(55, 32);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0xFFDD66,
      side: THREE.DoubleSide,
      fog: false,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
    });
    this.sunGlow = new THREE.Mesh(glowGeo, glowMat);
    this.sunGlow.renderOrder = -2;
    this.sunGlow.frustumCulled = false;
    scene.add(this.sunGlow);

    // ---- Moon mesh with phase support ----
    this._buildMoon(scene);

    // ---- Stars with twinkling ----
    this._buildStars(scene);

    // ---- Clouds ----
    this._createClouds(scene);

    this.baseFogNear = fog.near;
    this.baseFogFar = fog.far;
    this._underwaterOverride = false;
  }

  // ---- Moon with phase rendering ----
  _buildMoon(scene) {
    // Moon: full disc (always drawn) + shadow overlay (for phase)
    const moonGeo = new THREE.CircleGeometry(22, 32);
    const moonMat = new THREE.MeshBasicMaterial({
      color: 0xDDDDFF,
      side: THREE.DoubleSide,
      fog: false,
      transparent: true,
      depthWrite: false,
    });
    this.moonMesh = new THREE.Mesh(moonGeo, moonMat);
    this.moonMesh.renderOrder = -1;
    this.moonMesh.frustumCulled = false;
    scene.add(this.moonMesh);

    // Moon shadow overlay — a dark disc offset to create crescent effect
    const shadowGeo = new THREE.CircleGeometry(22, 32);
    const shadowMat = new THREE.MeshBasicMaterial({
      color: 0x050515,
      side: THREE.DoubleSide,
      fog: false,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    });
    this._moonShadow = new THREE.Mesh(shadowGeo, shadowMat);
    this._moonShadow.renderOrder = 0; // render on top of moon
    this._moonShadow.frustumCulled = false;
    scene.add(this._moonShadow);

    this._currentMoonPhase = -1; // force first update
  }

  // ---- Stars with per-star twinkle data ----
  _buildStars(scene) {
    const starGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(STAR_COUNT * 3);
    this._starSizes = new Float32Array(STAR_COUNT);
    this._starTwinkleSpeed = new Float32Array(STAR_COUNT);
    this._starTwinkleOffset = new Float32Array(STAR_COUNT);
    this._starBaseBrightness = new Float32Array(STAR_COUNT);

    for (let i = 0; i < STAR_COUNT; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 500;
      positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = Math.abs(r * Math.sin(phi) * Math.sin(theta));
      positions[i * 3 + 2] = r * Math.cos(phi);

      // Per-star twinkle parameters
      this._starSizes[i] = 1.5 + Math.random() * 2.0;
      this._starTwinkleSpeed[i] = 1.0 + Math.random() * 4.0;   // different twinkle speeds
      this._starTwinkleOffset[i] = Math.random() * Math.PI * 2; // random phase offset
      this._starBaseBrightness[i] = 0.5 + Math.random() * 0.5;  // base brightness
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeo.setAttribute('size', new THREE.BufferAttribute(this._starSizes, 1));

    const starMat = new THREE.PointsMaterial({
      color: 0xFFFFFF,
      size: 2,
      sizeAttenuation: false,
      fog: false,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.stars = new THREE.Points(starGeo, starMat);
    this.stars.renderOrder = -3;
    this.stars.frustumCulled = false;
    scene.add(this.stars);
  }

  // ---- Cloud layer generation ----
  _createClouds(scene) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = 'rgba(0,0,0,0)';
    ctx.fillRect(0, 0, 512, 512);
    ctx.fillStyle = 'rgba(255,255,255,0.9)';

    const seed = 12345;
    let s = seed;
    const rand = () => { s = (s * 16807 + 0) % 2147483647; return s / 2147483647; };

    // More cloud blobs for denser coverage
    for (let i = 0; i < 80; i++) {
      const cx = rand() * 512;
      const cz = rand() * 512;
      const count = 4 + Math.floor(rand() * 6);
      for (let j = 0; j < count; j++) {
        const ox = cx + (rand() - 0.5) * 70;
        const oz = cz + (rand() - 0.5) * 50;
        const r = 18 + rand() * 35;
        ctx.beginPath();
        ctx.arc(ox, oz, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 2); // Tile for more coverage

    const cloudGeo = new THREE.PlaneGeometry(1200, 1200);
    const cloudMat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
      fog: false,
      depthWrite: false,
    });
    this.clouds = new THREE.Mesh(cloudGeo, cloudMat);
    this.clouds.rotation.x = -Math.PI / 2;
    this.clouds.renderOrder = -1;
    this.clouds.frustumCulled = false;
    scene.add(this.clouds);

    this._cloudOffset = 0;
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  update(dt, cameraPos) {
    this._elapsed += dt;
    this._totalElapsed += dt;
    const t = this.getTimeOfDay();

    this._updateSkyColor(t);
    this._updateLighting(t);
    this._updateCelestials(t, cameraPos);
    this._updateStars(t, cameraPos);
    this._updateMoonPhase(cameraPos);
    this._updateClouds(dt, t, cameraPos);

    // Sky dome follows camera
    if (cameraPos) {
      this.skyDome.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    }
  }

  getTimeOfDay() {
    return (this._elapsed % CYCLE_DURATION) / CYCLE_DURATION;
  }

  isNight() {
    const t = this.getTimeOfDay();
    return t > 0.6 && t < 0.9;
  }

  getSunlight() {
    const t = this.getTimeOfDay();
    return this._sunlightCurve(t);
  }

  setTimeOfDay(value) {
    this._elapsed = (value % 1) * CYCLE_DURATION;
  }

  skipToMorning() {
    this.setTimeOfDay(0.15);
  }

  setUnderwaterOverride(active) {
    this._underwaterOverride = active;
  }

  setVisible(visible) {
    this.skyDome.visible = visible;
    this.sunMesh.visible = visible;
    this.sunGlow.visible = visible;
    this.moonMesh.visible = visible;
    this._moonShadow.visible = visible;
    this.stars.visible = visible;
    this.clouds.visible = visible;
  }

  serialize() {
    return { elapsed: this._elapsed, totalElapsed: this._totalElapsed };
  }

  deserialize(data) {
    if (data && data.elapsed !== undefined) {
      this._elapsed = data.elapsed;
    }
    if (data && data.totalElapsed !== undefined) {
      this._totalElapsed = data.totalElapsed;
    } else if (data && data.elapsed !== undefined) {
      this._totalElapsed = data.elapsed;
    }
  }

  // ------------------------------------------------------------------
  // Internal
  // ------------------------------------------------------------------

  _sunlightCurve(t) {
    const raw = Math.cos(2 * Math.PI * (t - 0.25));
    return raw * 0.5 + 0.5;
  }

  _updateSkyColor(t) {
    const color = this._skyColor;
    const zenith = this._zenithColor;

    // Horizon color
    if (t < 0.05) {
      color.copy(SKY_DAWN);
      zenith.copy(ZENITH_DAWN);
    } else if (t < 0.15) {
      const f = this._smooth((t - 0.05) / 0.10);
      color.copy(SKY_DAWN).lerp(SKY_DAY, f);
      zenith.copy(ZENITH_DAWN).lerp(ZENITH_DAY, f);
    } else if (t < 0.40) {
      color.copy(SKY_DAY);
      zenith.copy(ZENITH_DAY);
    } else if (t < 0.50) {
      const f = this._smooth((t - 0.40) / 0.10);
      color.copy(SKY_DAY).lerp(SKY_DUSK, f);
      zenith.copy(ZENITH_DAY).lerp(ZENITH_DUSK, f);
    } else if (t < 0.55) {
      color.copy(SKY_DUSK);
      zenith.copy(ZENITH_DUSK);
    } else if (t < 0.65) {
      const f = this._smooth((t - 0.55) / 0.10);
      color.copy(SKY_DUSK).lerp(SKY_NIGHT, f);
      zenith.copy(ZENITH_DUSK).lerp(ZENITH_NIGHT, f);
    } else if (t < 0.90) {
      color.copy(SKY_NIGHT);
      zenith.copy(ZENITH_NIGHT);
    } else {
      const f = this._smooth((t - 0.90) / 0.10);
      color.copy(SKY_NIGHT).lerp(SKY_DAWN, f);
      zenith.copy(ZENITH_NIGHT).lerp(ZENITH_DAWN, f);
    }

    // Update sky dome gradient
    this._skyUniforms.bottomColor.value.copy(color);
    this._skyUniforms.topColor.value.copy(zenith);

    // Dynamic glow color: warm orange at dawn/dusk, muted during day
    const sunAngle = Math.PI * 2 * t;
    const sunY = Math.sin(sunAngle);
    const horizonProximity = 1.0 - Math.abs(sunY);
    // Shift glow color warmer/pinker at sunset
    if (t > 0.40 && t < 0.65) {
      this._skyUniforms.sunGlowColor.value.setHex(0xFF6633); // warm sunset
    } else if (t < 0.10 || t > 0.90) {
      this._skyUniforms.sunGlowColor.value.setHex(0xFFBB55); // warm sunrise
    } else {
      this._skyUniforms.sunGlowColor.value.setHex(0xFFAA44); // normal
    }

    // Fog still uses horizon color
    if (!this._underwaterOverride) {
      this.fog.color.copy(color);
    }
  }

  _updateLighting(t) {
    const sunlight = this._sunlightCurve(t);
    this.ambientLight.intensity = THREE.MathUtils.lerp(0.1, 0.6, sunlight);
    this.directionalLight.intensity = THREE.MathUtils.lerp(0.2, 0.8, sunlight);
    this._tmpColor.copy(MOON_COLOR).lerp(SUN_COLOR, sunlight);
    this.directionalLight.color.copy(this._tmpColor);
  }

  _updateCelestials(t, cameraPos) {
    const sunAngle = Math.PI * 2 * t;
    const sunX = Math.cos(sunAngle) * ORBIT_RADIUS;
    const sunY = Math.sin(sunAngle) * ORBIT_RADIUS;

    const cx = cameraPos ? cameraPos.x : 0;
    const cy = cameraPos ? cameraPos.y : 0;
    const cz = cameraPos ? cameraPos.z : 0;

    this.sunMesh.position.set(cx + sunX, cy + sunY, cz);
    this.sunMesh.lookAt(cx, cy, cz);
    this.sunGlow.position.copy(this.sunMesh.position);
    this.sunGlow.lookAt(cx, cy, cz);

    this.moonMesh.position.set(cx - sunX, cy - sunY, cz);
    this.moonMesh.lookAt(cx, cy, cz);

    // Visibility
    this.sunMesh.visible = sunY > -20;
    this.sunGlow.visible = sunY > -20;
    this.moonMesh.visible = sunY < 20;
    this._moonShadow.visible = sunY < 20;

    // Fade near horizon
    const sunOpacity = THREE.MathUtils.clamp(sunY / 60, 0, 1);
    const moonOpacity = THREE.MathUtils.clamp(-sunY / 60, 0, 1);
    this.sunMesh.material.opacity = sunOpacity;
    this.sunGlow.material.opacity = sunOpacity * 0.3;
    this.moonMesh.material.opacity = moonOpacity;
    this._moonShadow.material.opacity = moonOpacity * 0.95;

    // Sun glow in sky dome shader
    const sunDir = new THREE.Vector3(sunX, sunY, 0).normalize();
    this._skyUniforms.sunDir.value.copy(sunDir);
    // Glow strongest at dawn/dusk
    const horizonFactor = 1.0 - Math.abs(sunY) / ORBIT_RADIUS;
    this._skyUniforms.sunGlowIntensity.value = horizonFactor * horizonFactor * 0.6;

    // Light direction
    if (sunY > 0) {
      this.directionalLight.position.set(sunX, sunY, 0).normalize().multiplyScalar(200);
    } else {
      this.directionalLight.position.set(-sunX, -sunY, 0).normalize().multiplyScalar(200);
    }
  }

  _updateStars(t, cameraPos) {
    const sunAngle = Math.PI * 2 * t;
    const sunY = Math.sin(sunAngle) * ORBIT_RADIUS;
    const baseStarOpacity = THREE.MathUtils.clamp((-sunY - 40) / 80, 0, 1);

    const isVisible = baseStarOpacity > 0.01;
    this.stars.visible = isVisible;

    if (isVisible) {
      // Twinkling: animate per-star size based on time
      const time = this._totalElapsed;
      const sizes = this._starSizes;
      const sizeAttr = this.stars.geometry.getAttribute('size');

      for (let i = 0; i < STAR_COUNT; i++) {
        const speed = this._starTwinkleSpeed[i];
        const offset = this._starTwinkleOffset[i];
        const baseBright = this._starBaseBrightness[i];

        // Twinkle via size variation (simulates brightness flicker)
        const twinkle = baseBright + (1.0 - baseBright) * (Math.sin(time * speed + offset) * 0.5 + 0.5);
        sizeAttr.array[i] = sizes[i] * twinkle;
      }
      sizeAttr.needsUpdate = true;

      // Overall opacity still controlled by day/night
      this.stars.material.opacity = baseStarOpacity;
    }

    if (cameraPos) {
      this.stars.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    }
  }

  /**
   * Update moon phase based on total game days elapsed.
   * 8 phases cycle every 8 game days.
   * Phase 0 = full moon, 4 = new moon, etc.
   */
  _updateMoonPhase(cameraPos) {
    const totalDays = Math.floor(this._totalElapsed / CYCLE_DURATION);
    const phase = totalDays % MOON_PHASE_COUNT;

    // Position shadow to always follow moon
    this._moonShadow.position.copy(this.moonMesh.position);
    this._moonShadow.lookAt(
      cameraPos ? cameraPos.x : 0,
      cameraPos ? cameraPos.y : 0,
      cameraPos ? cameraPos.z : 0
    );

    if (phase === this._currentMoonPhase) return;
    this._currentMoonPhase = phase;

    // Phase determines shadow visibility and offset:
    // 0 = full moon (no shadow)
    // 1-3 = waning (shadow from left)
    // 4 = new moon (full shadow)
    // 5-7 = waxing (shadow from right)
    if (phase === 0) {
      // Full moon: hide shadow
      this._moonShadow.scale.set(0, 0, 0);
    } else if (phase === 4) {
      // New moon: shadow covers entirely
      this._moonShadow.scale.set(1, 1, 1);
      this._moonShadow.position.copy(this.moonMesh.position);
    } else {
      // Partial phases: offset shadow to create crescent
      this._moonShadow.scale.set(1, 1, 1);
      // Calculate horizontal offset relative to moon face
      // Phases 1-3: shadow moves in from right (waning)
      // Phases 5-7: shadow moves in from left (waxing)
      let offsetFraction;
      if (phase <= 3) {
        offsetFraction = (phase / 4) * 0.8; // 0 to 0.6
      } else {
        offsetFraction = ((MOON_PHASE_COUNT - phase) / 4) * 0.8;
      }
      // Direction: waning = shadow from left (+x local), waxing = from right (-x)
      const direction = phase <= 3 ? -1 : 1;
      // We apply offset by shifting the shadow slightly from moon center
      // The offset is in world space along the "right" vector of the moon face
      const moonRight = new THREE.Vector3();
      this.moonMesh.getWorldDirection(moonRight);
      // Cross with up to get right vector
      const up = new THREE.Vector3(0, 1, 0);
      moonRight.crossVectors(up, moonRight).normalize();

      const offset = direction * offsetFraction * 22; // 22 = moon radius
      this._moonShadow.position.copy(this.moonMesh.position);
      this._moonShadow.position.addScaledVector(moonRight, offset);
    }

    // Adjust moon brightness by phase (full moon brighter)
    const brightness = phase === 0 ? 1.0 : phase === 4 ? 0.15 : 0.4 + 0.3 * (1 - Math.abs(phase - 4) / 4);
    this.moonMesh.material.color.setRGB(
      0.87 * brightness,
      0.87 * brightness,
      1.0 * brightness
    );
  }

  _updateClouds(dt, t, cameraPos) {
    this._cloudOffset += dt * 2;

    const cx = cameraPos ? cameraPos.x : 0;
    const cz = cameraPos ? cameraPos.z : 0;

    this.clouds.position.set(cx + this._cloudOffset * 0.3, CLOUD_HEIGHT, cz);

    const sunlight = this._sunlightCurve(t);
    this.clouds.material.opacity = THREE.MathUtils.lerp(0.2, 0.85, sunlight);
    this.clouds.visible = true;
  }

  _smooth(x) {
    return x * x * (3 - 2 * x);
  }
}
